import { ReactElement } from "react";
import {
  Badge,
  Box,
  Flex,
  Group,
  Stack,
  Text,
  useMantineTheme,
} from "@mantine/core";
import IconArrowRight from "@common/components/icons/IconArrowRight";
import { IPurchaseRowProps } from "./types";
import { captureException } from "@sentry/nextjs";
import { usePlan } from "@stores/plans/hooks";

export function PurchaseRow({ purchase }: IPurchaseRowProps): ReactElement {
  const { id, hosted_instance, product } = purchase;
  const { plan } = product;

  // yearly plan
  const yearlyPlanObj = usePlan({
    alias: `${plan.alias.replace("-yearly", "")}-yearly`,
  });

  // styles
  const theme = useMantineTheme();

  // no matching plan
  if (!yearlyPlanObj) {
    captureException(
      `Invoicing, ConfirmationModal: no matching plan found for plan with alias: ${plan.alias}`,
    );
    return <></>;
  }

  return (
    <Stack data-testid={id} spacing="xs" w="100%">
      <Text c="gray.5" fw={700} fz="xs">
        {hosted_instance?.alias_url || plan.description}
      </Text>

      <Flex
        align="center"
        direction="row"
        gap="xl"
        justify="flex-start"
        wrap="nowrap"
        w="100%"
      >
        <Box w="50%">
          <Text c="gray.5" fw={700} fz="xs">
            {plan.price}/month
          </Text>
          <Text c="gray.4" fw={400} fz="xs">
            Incl. {plan.users_included} users, then {plan.per_user_price}
            /user/month
          </Text>
        </Box>

        <Flex
          align="center"
          direction="row"
          justify="flex-start"
          w={{ base: "10%", sm: "auto" }}
        >
          <IconArrowRight color={theme.colors.yellow[3]} />
        </Flex>

        <Box w="50%">
          <Group spacing={theme.spacing.xs / 2}>
            <Text c="gray.5" fw={700} fz="xs">
              ${yearlyPlanObj.price}/year
            </Text>
            <Badge
              color="yellow.2"
              radius="sm"
              variant="outline"
              bg="yellow.0"
              c="yellow.3"
              fw={700}
              lh={`${theme.fontSizes.sm}px`}
              h="auto"
              fz={theme.fontSizes.lg / 2}
              lts={-0.25}
              py={0}
              tt="none"
            >
              Saves 10%
            </Badge>
          </Group>
          <Text c="gray.4" fw={400} fz="xs">
            Incl. {yearlyPlanObj.users_included} users, then $
            {yearlyPlanObj.per_user_price}/user/year
          </Text>
        </Box>
      </Flex>
    </Stack>
  );
}
